import React from 'react';
import styled from '@emotion/styled';
import { palette, radius, spacing, typography } from '../../style-utils/theme';

/**
 * THE ONE ERROR BOUNDARY THE APP DID NOT HAVE.
 *
 * ══════════════════ WHY IT EXISTS, WRITTEN HONESTLY ══════════════════
 *
 * A crash was reported during shift handover:
 *
 *     Uncaught TypeError: Cannot read properties of undefined (reading
 *     'startTime') at et.reportAllChanges (<anonymous>:2:19429)
 *
 * That trace is NOT this application. Three things say so and they agree:
 *   · `reportAllChanges` appears nowhere in this repository — not in src, not
 *     in the production build, not in any bundled dependency's shipped code.
 *   · Every frame is `<anonymous>`, and the console prefixed the repeat with
 *     `VM88:` — which is what DevTools names a script created by eval or
 *     `new Function`. Code from our bundle names the bundle.
 *   · `reportWebVitals()` in `src/index.tsx` is called with NO handler, so the
 *     dynamic `import('web-vitals')` inside it never runs at all.
 *
 * The overwhelmingly likely source is an injected script — a browser extension
 * (a Web Vitals / RUM one, whose LCP handler reads `entries[last].startTime`).
 * Nothing in this file can stop that script throwing, and it would be dishonest
 * to claim otherwise.
 *
 * ══════════════════ SO WHAT IS THIS ACTUALLY FOR ══════════════════
 *
 * The audit that chased that trace found something worse than the trace: this
 * application had NO error boundary anywhere. `componentDidCatch` and
 * `getDerivedStateFromError` did not appear once in `src`. So ANY render-time
 * throw — one bad row, one undefined field, one library upgrade — unmounted the
 * entire React tree and left a white screen with no message, no way back, and
 * nothing in the UI to report. On a front desk, mid-shift, that is the desk
 * going dark.
 *
 * ══════════════════ WHAT IT DOES AND DOES NOT CATCH ══════════════════
 *
 * CATCHES: a throw during render, in a lifecycle method, or in a constructor,
 * anywhere below it.
 *
 * DOES NOT CATCH — and no boundary can: event handlers, `setTimeout`, promise
 * rejections, errors thrown by scripts outside React's tree (including the
 * injected one above), or errors during server-side rendering. Those are what
 * the `window.onerror` listener in `src/index.tsx` reports; they are logged, not
 * repaired, because a handler that swallows them would hide real defects.
 *
 * ══════════════════ IT OFFERS A WAY OUT, NOT AN APOLOGY ══════════════════
 *
 * "Try again" re-mounts the subtree, which recovers a transient failure without
 * losing the session. "Reload" is the honest second option when it does not.
 * The message says what is true — the last thing recorded is safe, because
 * everything this product writes is written server-side before the screen
 * changes — and it shows the error text, because a concierge reading it to a
 * manager is how this gets fixed.
 */

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  align-items: flex-start;
  max-width: 560px;
  margin: ${spacing.xl} auto;
  padding: ${spacing.md};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${typography.display};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  color: ${palette.ink};
`;

const Text = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.inkSoft};
`;

const Detail = styled.pre`
  margin: 0;
  padding: ${spacing.xs};
  width: 100%;
  box-sizing: border-box;
  overflow-x: auto;
  background: ${palette.surfaceMuted};
  border-radius: ${radius.sm};
  font-family: ${typography.mono};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
  white-space: pre-wrap;
  word-break: break-word;
`;

const Row = styled.div`
  display: flex;
  gap: ${spacing.xs};
  flex-wrap: wrap;
`;

const Action = styled.button`
  -webkit-appearance: none;
  appearance: none;
  min-height: 44px;
  padding: 0 ${spacing.md};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
`;

interface Props {
  children: React.ReactNode;
  /** Named in the message, so a report says WHICH screen went down. */
  label?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // The console is the only sink this product has today. It is deliberate
    // that this does not swallow: a boundary that logs nothing turns a crash
    // into a mystery, and the component stack is the part that names the file.
    // eslint-disable-next-line no-console
    console.error('[Lobbero] render error', error, info.componentStack);
  }

  private retry = (): void => {
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const where = this.props.label ? ` on ${this.props.label}` : '';

    return (
      <Box role="alert">
        <Title>Something went wrong{where}</Title>
        <Text>
          This screen stopped drawing. Nothing you had already recorded is
          affected — every entry, parcel and shift is written on the server
          before the screen changes, so the log is intact.
        </Text>
        <Text>
          Try again first. If it keeps happening, reload, and give the line below
          to whoever is looking after this — it is what identifies the fault.
        </Text>
        <Detail>{error.message || String(error)}</Detail>
        <Row>
          <Action type="button" onClick={this.retry}>
            Try again
          </Action>
          <Action type="button" onClick={() => window.location.reload()}>
            Reload
          </Action>
        </Row>
      </Box>
    );
  }
}
